import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from '@doccareplus/i18n';
import { Button, ConfirmDialog } from '@doccareplus/ui';
import { logout, useAppDispatch } from '@doccareplus/kernel/application';
import { authApi } from '@doccareplus/kernel/infrastructure/api/auth.service';
import { applyActiveProfileHeader, clearPortalPatientData, PortalProfileSwitcher } from '@/features/profiles';
import { PORTAL_TABS } from '../portalNavModel';

export function PortalMobileShell() {
  const { t } = useTranslation('portal');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [signOutConfirming, setSignOutConfirming] = useState(false);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Best-effort server logout; the client session is cleared regardless.
    }
    applyActiveProfileHeader(null);
    await clearPortalPatientData(queryClient, { includeAccountData: true });
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-[100dvh] justify-center bg-[var(--bg)] text-[var(--text)]">
      <div className="flex min-h-[100dvh] w-full max-w-[430px] flex-col bg-[var(--bg)]">
        <header className="sticky top-0 z-20 flex items-center gap-2.5 border-b border-[var(--border)] bg-[var(--surface)] px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <PortalProfileSwitcher compact onFindProfiles={() => navigate('/profile')} />
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('signOut', 'Sign out')}
            className="h-10 w-10 shrink-0 px-0"
            onClick={() => setSignOutConfirming(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10 17l5-5-5-5" />
              <path d="M15 12H3" />
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
            </svg>
          </Button>
        </header>

        <main className="flex-1 overflow-y-auto px-4 pb-24 pt-4">
          <Outlet />
        </main>

        <nav aria-label={t('navigation', 'Portal navigation')} className="fixed inset-x-0 bottom-0 z-20 mx-auto flex max-w-[430px] border-t border-[var(--border)] bg-[var(--surface)] pb-[max(8px,env(safe-area-inset-bottom))] pt-2">
          {PORTAL_TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                `flex min-h-[52px] flex-1 flex-col items-center justify-center gap-1 text-[10.5px] font-medium no-underline hover:no-underline ${
                  isActive ? 'text-[var(--primary)] font-bold' : 'text-[var(--text-3)]'
                }`
              }
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {tab.paths.map((d, i) => <path key={i} d={d} />)}
              </svg>
              {t(tab.labelKey, tab.fallback)}
            </NavLink>
          ))}
        </nav>
      </div>

      {signOutConfirming && (
        <ConfirmDialog
          title={t('signOutConfirmTitle', 'Sign out?')}
          description={t('signOutConfirmDescription', "You'll need to sign in again to access your portal account.")}
          confirmLabel={t('signOut', 'Sign out')}
          cancelLabel={t('staySignedIn', 'Stay signed in')}
          onConfirm={handleLogout}
          onCancel={() => setSignOutConfirming(false)}
        />
      )}
    </div>
  );
}
